<!--
  `translate="no"` is a PATIENT-SAFETY guard, not a preference.

  Chrome's page translator rewrites the DOM by replacing the text nodes Angular created for its
  interpolations. Angular keeps direct references to those nodes, so once they are swapped every
  later update writes to a node that is no longer in the document: the value on screen FREEZES at
  whatever the translator captured, while the form state moves on. Measured on the CHF guide with
  the page translated to Spanish — "Change since yesterday" stayed at "+169,0 libras" (the delta
  while "Weight yesterday" briefly held just the "1" of "168") next to a correctly self-ticked
  "gain >=2 lbs" box, because a checkbox's checked state is a DOM property and survives the swap
  while the delta beside it does not. An MA reading that row is reading a number the guide is no
  longer computing.

  Scoped to the guide rather than declared globally in index.html: this subtree is the one that
  renders derived clinical numbers, and a blanket `<meta name="google" content="notranslate">`
  would take translation away from the rest of the platform, which is a product call.
-->
<div class="care-call-guide notranslate" translate="no">

  <mat-progress-bar *ngIf="loading" mode="indeterminate" class="care-call-guide__loader"></mat-progress-bar>

  <ng-container *ngIf="!loading && guide">
    <div class="care-call-guide__header">
      <h2 class="care-call-guide__title">{{ displayTitle }}</h2>
    </div>

    <!--
      BRD-CLIN-2026-006 §8.2 — the diuretic titration exclusions, shown where the decision is made.

      NFR-5.3 requires the system not to present dose escalation beyond the LVAD / ESRD / eGFR<30
      criteria. Nothing on this guide offers a dose change yet, so this banner does not gate a
      control — it puts the gate in front of the clinician who is on the call and may be asked about
      one. Rendered ABOVE the form rather than beside the medication section on purpose: an exclusion
      is a fact about the patient, not about one answer, and it must be readable before the MA starts
      rather than found on the way past.

      The blocked state is a WARNING, not an error. Being excluded is a normal, correct outcome for a
      dialysis patient, and styling it as a failure would train people to dismiss it.
    -->
    <div
      *ngIf="showsDiureticGate"
      class="care-call-guide__eligibility"
      [class.care-call-guide__eligibility--blocked]="diureticGateBlocked"
      role="status"
    >
      <mat-icon class="care-call-guide__eligibility-icon">{{ diureticGateBlocked ? 'warning_amber' : 'verified' }}</mat-icon>
      <span class="care-call-guide__eligibility-text">{{ diureticGateLabel }}</span>
    </div>

    <form [formGroup]="guideForm">
      <mat-card *ngFor="let section of sortedSections" class="care-call-guide__section">
        <mat-card-header>
          <mat-card-title class="care-call-guide__section-title">
            <mat-icon *ngIf="section.escalation_zone" class="care-call-guide__section-warn" matTooltip="Requires escalation to NCM if threshold met">warning</mat-icon>
            {{ section.label }}
          </mat-card-title>
          <mat-card-subtitle *ngIf="section.description" class="care-call-guide__section-desc">
            {{ section.description }}
          </mat-card-subtitle>
        </mat-card-header>

        <mat-card-content [formGroupName]="section.id">
          <ng-container *ngFor="let field of section.fields">
            <div
              *ngIf="isFieldVisible(section, field) && (field.type !== 'computed' || computedDisplay(section, field))"
              class="care-call-guide__field"
              [class.care-call-guide__field--computed]="field.type === 'computed'"
            >

              <ng-container [ngSwitch]="field.type">

                <!-- Read-only script / instructions -->
                <div *ngSwitchCase="'info'" class="care-call-guide__info">
                  <strong *ngIf="field.label" class="care-call-guide__info-label">{{ field.label }}</strong>
                  <span class="care-call-guide__info-text">{{ field.helper_text }}</span>
                </div>

                <!-- Read-only derived value (e.g. weight change vs yesterday / last week). Hidden until both operands are filled. -->
                <ng-container *ngSwitchCase="'computed'">
                  <span *ngIf="computedDisplay(section, field) as display" class="care-call-guide__computed">
                    <span class="care-call-guide__computed-label">{{ field.label }}:</span>
                    <span
                      class="care-call-guide__computed-value"
                      [class.care-call-guide__computed-value--alert]="computedIsRed(section, field)"
                      >{{ display }}</span
                    >
                  </span>
                </ng-container>

                <ng-container *ngSwitchCase="'checkbox'">
                  <!-- auto_check boxes are system-computed: non-interactive (no click/tab) but keep the normal checked color -->
                  <mat-checkbox
                    [formControlName]="field.id"
                    [class.care-call-guide__checkbox--auto]="!!field.auto_check"
                    [tabIndex]="field.auto_check ? -1 : 0"
                    >{{ field.label }}</mat-checkbox
                  >
                  <p *ngIf="field.helper_text" class="care-call-guide__helper">{{ field.helper_text }}</p>
                </ng-container>

                <ng-container *ngSwitchCase="'radio'">
                  <label class="care-call-guide__field-label">{{ field.label }}</label>
                  <p *ngIf="field.helper_text" class="care-call-guide__helper">{{ field.helper_text }}</p>
                  <mat-radio-group [formControlName]="field.id" class="care-call-guide__radio-group">
                    <mat-radio-button *ngFor="let opt of field.options" [value]="opt">{{ opt }}</mat-radio-button>
                  </mat-radio-group>
                </ng-container>

                <ng-container *ngSwitchCase="'text'">
                  <mat-form-field appearance="outline" class="care-call-guide__form-field">
                    <mat-label *ngIf="field.label">{{ field.label }}</mat-label>
                    <input
                      matInput
                      [formControlName]="field.id"
                      [placeholder]="field.placeholder || ''"
                      [attr.inputmode]="field.numeric_slash ? 'numeric' : null"
                      (input)="field.numeric_slash && sanitizeNumericSlash(section.id, field.id)"
                    />
                    <span matSuffix *ngIf="field.unit" class="care-call-guide__unit">{{ field.unit }}</span>
                  </mat-form-field>
                  <p *ngIf="field.helper_text" class="care-call-guide__helper">{{ field.helper_text }}</p>
                </ng-container>

                <ng-container *ngSwitchCase="'date'">
                  <mat-form-field appearance="outline" class="care-call-guide__form-field">
                    <mat-label>{{ field.label }}</mat-label>
                    <input matInput [matDatepicker]="picker" [min]="field.disallow_past ? today : null" [formControlName]="field.id" />
                    <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
                    <mat-datepicker #picker></mat-datepicker>
                  </mat-form-field>
                </ng-container>

                <ng-container *ngSwitchCase="'number'">
                  <mat-form-field appearance="outline" class="care-call-guide__form-field">
                    <mat-label>{{ field.label }}</mat-label>
                    <input matInput type="number" [formControlName]="field.id" />
                    <span matSuffix *ngIf="field.unit" class="care-call-guide__unit">{{ field.unit }}</span>
                  </mat-form-field>
                  <p *ngIf="field.helper_text" class="care-call-guide__helper">{{ field.helper_text }}</p>
                  <div *ngIf="field.escalate_gain_lbs" class="care-call-guide__reminder">
                    <mat-icon>info</mat-icon>
                    Flag if gain &ge; {{ field.escalate_gain_lbs }} lbs from last recorded weight
                  </div>
                </ng-container>

                <ng-container *ngSwitchCase="'select'">
                  <mat-form-field appearance="outline" class="care-call-guide__form-field">
                    <mat-label>{{ field.label }}</mat-label>
                    <mat-select [formControlName]="field.id">
                      <mat-option *ngFor="let opt of field.options" [value]="opt">{{ opt }}</mat-option>
                    </mat-select>
                  </mat-form-field>
                  <p *ngIf="field.helper_text" class="care-call-guide__helper">{{ field.helper_text }}</p>
                </ng-container>

                <ng-container *ngSwitchCase="'textarea'">
                  <label *ngIf="field.label" class="care-call-guide__field-label">{{ field.label }}</label>
                  <p *ngIf="field.helper_text" class="care-call-guide__helper">{{ field.helper_text }}</p>
                  <mat-form-field appearance="outline" class="care-call-guide__form-field">
                    <textarea matInput [formControlName]="field.id" [placeholder]="field.placeholder || ''" rows="3"></textarea>
                  </mat-form-field>
                </ng-container>

              </ng-container>

              <!-- Unified escalation alert (numeric thresholds + value-match triggers) -->
              <div *ngIf="field.type !== 'info' && field.type !== 'computed' && fieldEscalates(section, field)" class="care-call-guide__alert">
                <mat-icon>warning</mat-icon>
                {{ escalationMessage(field) }}
              </div>

              <!-- Live status banner computed from this field's own value (e.g. graded BP status) -->
              <div
                *ngIf="fieldStatus(section, field) as status"
                class="care-call-guide__status"
                [ngClass]="'care-call-guide__status--' + (status.severity || 'info')"
              >
                {{ status.message }}
              </div>

            </div>
          </ng-container>

          <!--
            Section-level STAT banner. Shown once, at the foot of the section, as soon as ANY field
            in it trips an EMERGENCY-tier trigger — it is a restatement of the instruction the
            tripped field's own alert already carries, put where it cannot be missed mid-call.
            It escalates nothing on its own.
          -->
          <div *ngIf="statProtocolTriggered(section)" class="care-call-guide__stat-protocol" role="alert">
            <mat-icon>warning</mat-icon>
            <span>INITIATE 911 PROTOCOL — pause the call, keep the patient on the line and alert the NCM immediately.</span>
          </div>
        </mat-card-content>
      </mat-card>
    </form>

    <div class="care-call-guide__actions">
      <button
        mat-raised-button
        color="primary"
        [disabled]="!hasFormData || generatingNote"
        (mousedown)="flushPendingInput()"
        (click)="generateNote()"
      >
        <mat-icon>auto_awesome</mat-icon>
        {{ generatingNote ? 'Generating...' : 'Generate Note' }}
      </button>
    </div>
  </ng-container>

  <div *ngIf="!loading && !guide" class="care-call-guide__empty">
    <mat-icon>error_outline</mat-icon>
    <p>Care call guide not available for this patient.</p>
  </div>

</div>
